import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import { ServiceIcon, DEFAULT_ICON } from '../constants/serviceIcons';
import { localizedPath } from '../utils/routes';
import PromotionBanner from '../components/PromotionBanner';
import SEOHead from '../components/SEOHead';
import JsonLd from '../components/JsonLd';

const API = process.env.REACT_APP_API_URL || '/api';

export default function Services() {
  const { t, language } = useLanguage();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const { mc } = useMarketingCopy();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    setActiveCategory('All');
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/services?lang=${language}`)
      .then(r => {
        if (!cancelled) {
          setServices(Array.isArray(r.data) ? r.data : []);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Services fetch failed:', err.message, err.response?.status);
          setError(err.response?.status ? `Error ${err.response.status}` : 'Network error — is the backend running?');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [language]);

  const categories = ['All', ...new Set(services.map(s => s.category))];
  const filtered = activeCategory === 'All' ? services : services.filter(s => s.category === activeCategory);

  if (loading) return <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:'#D42B2B', fontFamily:'Rajdhani', fontSize:'1.5rem', letterSpacing:'0.2em' }}>{t.common.loading}</div>;

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', paddingTop: 'var(--cjpa-nav-offset)' }}>
      <SEOHead page="services" />
      {services.length > 0 && (
        <JsonLd data={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: services.map((s, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'Service',
              name: s.name,
              description: s.description,
              provider: { '@type': 'AutoRepair', name: 'Mécanique Auto CJPA' },
              ...(s.price_min && s.price_max ? {
                offers: {
                  '@type': 'AggregateOffer',
                  lowPrice: s.price_min,
                  highPrice: s.price_max,
                  priceCurrency: 'CAD'
                }
              } : {})
            }
          }))
        }} />
      )}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 3rem)' }}>
        <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '1rem' }}>{mc('services', 'subtitle', t.services.subtitle)}</div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(3rem, 6vw, 5rem)', color: '#E8ECF0', fontWeight: 300, marginBottom: '1.5rem' }}>
            {t.services.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.services.titleHighlight}</span>
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '0.85rem', maxWidth: '500px', margin: '0 auto', lineHeight: 1.8 }}>
            {mc('services', 'description', t.services.description)}
          </p>
        </div>

        {error && (
          <div style={{ textAlign: 'center', padding: '2rem', marginBottom: '2rem', background: 'rgba(212,43,43,0.1)', border: '1px solid rgba(212,43,43,0.3)' }}>
            <div style={{ color: '#D42B2B', fontSize: '1rem', marginBottom: '0.5rem' }}>{error}</div>
            <button onClick={() => window.location.reload()} style={{ background: '#D42B2B', color: '#0C1220', border: 'none', padding: '0.5rem 1.5rem', cursor: 'pointer', fontFamily: "'Barlow', sans-serif", fontSize: '0.75rem', letterSpacing: '0.08em' }}>
              {t.common.retry || 'Retry'}
            </button>
          </div>
        )}

        <PromotionBanner placement="services_top" />

        {/* Services header accent strip */}
        <div style={{ position: 'relative', overflow: 'hidden', height: '120px', marginBottom: '3rem' }}>
          <img
            src="/images/services/services-header-strip.jpg"
            alt={t.images?.servicesHeader || ''}
            width={1200}
            height={120}
            loading="lazy"
            style={{ width: '100%', height: '120px', objectFit: 'cover', filter: 'brightness(0.5)', display: 'block' }}
            onError={e => { e.target.closest('div[style]').style.display = 'none'; }}
          />
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'linear-gradient(to right, #0C1220, transparent 20%, transparent 80%, #0C1220)',
          }} />
        </div>

        {/* Category Filter */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '3.5rem', flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} style={{
              background: 'transparent', border: activeCategory === cat ? '1px solid #D42B2B' : '1px solid rgba(148,163,184,0.2)',
              color: activeCategory === cat ? '#D42B2B' : '#94A3B8', padding: '0.5rem 1.5rem',
              fontFamily: "'Barlow', sans-serif", fontSize: '0.65rem', letterSpacing: '0.08em',
              textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s',
            }}>{cat === 'All' ? t.services.allCategory : cat}</button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '1px', background: 'rgba(148,163,184,0.08)' }}>
          {filtered.map(s => (
            <div key={s.id} style={{ background: '#111827', padding: '3rem 2.5rem', display: 'flex', flexDirection: 'column', transition: 'background 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#111827'}
              onMouseLeave={e => e.currentTarget.style.background = '#111827'}>
              <div style={{ marginBottom: '1.2rem' }}><ServiceIcon iconKey={s.image_url || DEFAULT_ICON} size={36} /></div>
              <div style={{ fontSize: '0.58rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{s.category}</div>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.6rem', color: '#E8ECF0', marginBottom: '1rem', fontWeight: 500 }}>{s.name}</h3>
              <p style={{ color: '#94A3B8', fontSize: '0.82rem', lineHeight: 1.8, marginBottom: '0' }}>{s.description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid rgba(148,163,184,0.1)', paddingTop: '1.2rem', marginTop: 'auto' }}>
                <div>
                  <div style={{ color: '#D42B2B', fontSize: '1.1rem', fontFamily: "'Rajdhani', sans-serif" }}>
                    {s.price_min && s.price_max ? `$${s.price_min} – $${s.price_max}` : t.services.callForPricing}
                  </div>
                  <div style={{ color: '#64748B', fontSize: '0.68rem', letterSpacing: '0.1em', marginTop: '0.2rem' }}>{t.services.estimated}: {s.duration_minutes} {t.services.minutes}</div>
                </div>
                <Link to={lp('book')} style={{
                  color: '#D42B2B', textDecoration: 'none', fontSize: '0.65rem',
                  letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Barlow', sans-serif",
                  border: '1px solid rgba(148,163,184,0.3)', padding: '0.6rem 1.2rem',
                }}>{t.services.bookButton}</Link>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '5rem', padding: '3rem', background: 'rgba(148,163,184,0.03)', border: '1px solid rgba(148,163,184,0.1)' }}>
          <p style={{ color: '#94A3B8', fontSize: '0.85rem', lineHeight: 1.8, marginBottom: '1.5rem' }}>
            {mc('services', 'dontSee', t.services.dontSee)}
          </p>
          <Link to={lp('book')} style={{
            display: 'inline-block', background: '#D42B2B', color: '#0C1220',
            padding: '1rem 2.5rem', textDecoration: 'none',
            fontFamily: "'Barlow', sans-serif", fontSize: '0.65rem',
            letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
          }}>{mc('services', 'scheduleButton', t.services.scheduleButton)}</Link>
        </div>
      </div>
    </div>
  );
}
